import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Info, Plus } from 'lucide-react';
import { EntryTypeRow } from './entry-type-row';

// The per-queue Entry types tab: an explainer banner, a sortable list of entry types, and an add
// affordance. All edits flow back into the queue draft via the callbacks from the editor pane.
export function EntryTypesSection({ entryTypes, disabled = false, onChange, onDelete, onAdd, onReorder }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = entryTypes.map((t) => t.id);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) onReorder(active.id, over.id);
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2.5 rounded-lg bg-primary/10 px-4 py-3 ring-1 ring-primary/25">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
        <p className="text-[12.5px] leading-relaxed text-foreground">
          When <b>Show to doctor</b> is off, the entry stays on the staff board but is hidden from the
          doctor view — used for medication pickups, consent signing and other non-clinical visits.
        </p>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={handleDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {entryTypes.map((type) => (
              <EntryTypeRow
                key={type.id}
                type={type}
                disabled={disabled}
                onChange={(patch) => onChange(type.id, patch)}
                onDelete={() => onDelete(type.id)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {entryTypes.length === 0 ? (
        <div className="rounded-md border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground">
          No entry types yet. Add one to classify why patients are in the queue.
        </div>
      ) : null}

      {!disabled ? (
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex w-fit items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-[12.5px] font-semibold text-muted-foreground transition hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
          Add entry type
        </button>
      ) : null}
    </div>
  );
}
